import {DriveEntry, DriveFolder} from '@app/drive/files/drive-entry';
import {DriveApiIndexParams} from '@app/drive/files/queries/use-active-page-entries';
import type {UserFolderPathParams} from '@app/drive/files/queries/use-folder-path';
import type {
  UserFoldersApiParams,
  UserFoldersResponse,
} from '@app/drive/files/queries/use-folders';
import type {FetchShareableLinkResponse} from '@app/drive/shareable-link/queries/use-entry-shareable-link';
import {
  hasNextPage,
  LengthAwarePaginationResponse,
} from '@common/http/backend-response/pagination-response';
import {queryFactoryHelpers} from '@common/http/queries-file-helpers';
import {infiniteQueryOptions, queryOptions} from '@tanstack/react-query';
import {BootstrapData} from '@ui/bootstrap-data/bootstrap-data';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

const get = queryFactoryHelpers.get;

export interface FileEntryActivityUser {
  id: number;
  name: string;
  email: string;
  image?: string | null;
}

export interface FileEntryActivityEntry {
  id: number;
  name: string;
  type: string;
  workspace_id?: number | null;
}

export interface FileEntryActivity {
  id: number;
  entry_id: number;
  workspace_id: number;
  user_id?: number | null;
  action: string;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  user?: FileEntryActivityUser | null;
  entry?: FileEntryActivityEntry | null;
}

export interface FetchFileEntryActivitiesResponse {
  activities: FileEntryActivity[];
}

export interface WorkspaceStorageInsightEntry {
  id: number;
  name: string;
  type: string;
  mime?: string | null;
  extension?: string | null;
  file_size: number;
  updated_at?: string | null;
}

export interface WorkspaceStorageInsightType {
  type: string;
  count: number;
  bytes: number;
}

export interface FetchWorkspaceStorageInsightsResponse {
  total_bytes: number;
  file_count: number;
  folder_count: number;
  largest_files: WorkspaceStorageInsightEntry[];
  oldest_files: WorkspaceStorageInsightEntry[];
  largest_folders: WorkspaceStorageInsightEntry[];
  type_breakdown: WorkspaceStorageInsightType[];
}

export interface PersonalDriveInsightEntry {
  id: number;
  name: string;
  type: string;
  hash: string;
  mime?: string | null;
  extension?: string | null;
  file_size: number;
  folder_color?: string | null;
  folder_icon?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
}

export interface FetchPersonalDriveInsightsResponse {
  total_bytes: number;
  file_count: number;
  folder_count: number;
  trash_count: number;
  trash_bytes: number;
  oldest_deleted_at?: string | null;
  pinned_folders: PersonalDriveInsightEntry[];
  recent_files: PersonalDriveInsightEntry[];
  largest_folders: PersonalDriveInsightEntry[];
}

type StorageSummaryState =
  | 'ready'
  | 'stale'
  | 'queued'
  | 'processing'
  | 'failed'
  | 'unknown';

export interface FetchStorageSummaryResponse {
  used: number;
  available: number | null;
  state: StorageSummaryState;
  quotaBytes?: number | null;
  trashBytes: number;
  filesCount: number;
  foldersCount: number;
  imagesCount: number;
  videosCount: number;
  otherCount: number;
  calculatedAt?: string | null;
}

interface DriveStorageUsagePayload {
  usedBytes: number;
  quotaBytes: number | null;
  trashBytes: number;
  filesCount: number;
  foldersCount: number;
  imagesCount: number;
  videosCount: number;
  otherCount: number;
  calculatedAt: string | null;
  state: Exclude<StorageSummaryState, 'unknown'>;
}

interface DriveStorageUsageResponse {
  status: 'success';
  usage: DriveStorageUsagePayload;
}

interface DriveStorageUsageLegacyResponse {
  used: number;
  available: number | null;
}

interface DriveStorageUsageProcessingResponse {
  state: 'processing';
  message?: string;
  retryAfterSeconds: number;
  status?: 'success';
}

type DriveStorageUsageRawResponse =
  | DriveStorageUsageResponse
  | DriveStorageUsageLegacyResponse
  | DriveStorageUsageProcessingResponse;

interface FolderSummaryPayload {
  folderId: number;
  directFiles: number;
  directFolders: number;
  recursiveFiles: number;
  recursiveFolders: number;
  recursiveSize: number;
  images: number;
  videos: number;
  others: number;
  calculatedAt: string | null;
  state: 'ready' | 'stale' | 'failed';
}

export interface DriveFolderSummaryResponse {
  status: 'success';
  folderId: number;
  summary: FolderSummaryPayload;
  refresh?: {
    state: 'queued';
  } | null;
}

interface DriveFolderSummaryProcessingResponse {
  status: 'success';
  folderId: number;
  recursive: boolean;
  state: 'processing';
  message?: string;
  retryAfterSeconds: number;
}

export type DriveFolderSummaryApiResponse =
  | DriveFolderSummaryResponse
  | DriveFolderSummaryProcessingResponse;

export interface TransferStatusResponse {
  status: 'success';
  transfer: {
    sessionId: string;
    type: 'camera_sync';
    state:
      | 'reconciling'
      | 'awaiting_upload'
      | 'processing'
      | 'partial'
      | 'completed';
    total: number;
    completed: number;
    failed: number;
    processing: number;
    missing: number;
    percentage: number;
    current?: {
      fileName?: string;
      entryId?: number | null;
    } | null;
    startedAt?: string | null;
    completedAt?: string | null;
    updatedAt?: string | null;
  };
}

function normalizeStorageSummaryResponse(
  response: DriveStorageUsageRawResponse,
): FetchStorageSummaryResponse {
  if ('usage' in response) {
    const usage = response.usage;
    return {
      used: usage.usedBytes,
      available: usage.quotaBytes,
      state: usage.state,
      quotaBytes: usage.quotaBytes,
      calculatedAt: usage.calculatedAt,
      trashBytes: usage.trashBytes,
      filesCount: usage.filesCount,
      foldersCount: usage.foldersCount,
      imagesCount: usage.imagesCount,
      videosCount: usage.videosCount,
      otherCount: usage.otherCount,
    };
  }

  if ('state' in response) {
    return {
      used: 0,
      available: null,
      state: 'processing',
      quotaBytes: null,
      calculatedAt: null,
      trashBytes: 0,
      filesCount: 0,
      foldersCount: 0,
      imagesCount: 0,
      videosCount: 0,
      otherCount: 0,
    };
  }

  return {
    used: response.used,
    available: response.available,
    state: 'ready',
    quotaBytes: response.available,
    calculatedAt: null,
    trashBytes: 0,
    filesCount: 0,
    foldersCount: 0,
    imagesCount: 0,
    videosCount: 0,
    otherCount: 0,
  };
}

export const driveInvalidateKey = ['drive'];

export const appQueries = {
  driveEntries: {
    invalidateKey: [...driveInvalidateKey, 'entries'],
    index: <T = DriveEntry>(params: DriveApiIndexParams) =>
      infiniteQueryOptions({
        queryKey: [...driveInvalidateKey, 'entries', 'index', params],
        queryFn: ({pageParam = 1, signal}) => {
          return get<LengthAwarePaginationResponse<T> & {folder?: DriveFolder}>(
            'drive/file-entries',
            {
              ...params,
              page: pageParam,
            },
            signal,
          );
        },
        initialPageParam: 1,
        getNextPageParam: lastResponse => {
          const currentPage = lastResponse.current_page;
          if (!hasNextPage(lastResponse)) {
            return undefined;
          }
          return currentPage + 1;
        },
      }),
    get: (id: number) =>
      queryOptions<{fileEntry: DriveEntry}>({
        queryKey: [...driveInvalidateKey, 'entries', 'get', id],
        queryFn: () => get(`drive/file-entries/${id}/model`),
      }),
  },
  storageSummary: {
    invalidateKey: [...driveInvalidateKey, 'storage-summary'],
    get: (workspaceId?: number | null) =>
      queryOptions<FetchStorageSummaryResponse>({
        queryKey: [...driveInvalidateKey, 'storage-summary', workspaceId ?? 0],
        queryFn: async () =>
          normalizeStorageSummaryResponse(
            await get<DriveStorageUsageRawResponse>('drive/storage/usage', {
              workspaceId: workspaceId ?? 0,
            }),
          ),
      }),
  },
  folderSummary: {
    invalidateKey: [...driveInvalidateKey, 'folder-summary'],
    get: (folderId: number, workspaceId?: number | null) =>
      queryOptions<DriveFolderSummaryApiResponse>({
        queryKey: [
          ...driveInvalidateKey,
          'folder-summary',
          folderId,
          workspaceId ?? 0,
        ],
        queryFn: () =>
          get<DriveFolderSummaryApiResponse>(
            `drive/folders/${folderId}/summary`,
            {workspaceId: workspaceId ?? 0},
          ),
      }),
  },
  transferStatus: {
    invalidateKey: [...driveInvalidateKey, 'transfer-status'],
    get: (sessionId: string, workspaceId?: number | null) =>
      queryOptions<TransferStatusResponse>({
        queryKey: [
          ...driveInvalidateKey,
          'transfer-status',
          sessionId,
          workspaceId ?? 0,
        ],
        queryFn: () =>
          get<TransferStatusResponse>('transfers/status', {
            sessionId,
            workspaceId: workspaceId ?? 0,
          }),
      }),
  },
  fileEntryActivities: {
    invalidateKey: [...driveInvalidateKey, 'entry-activities'],
    get: (entryId: number, workspaceId?: number | null) =>
      queryOptions<FetchFileEntryActivitiesResponse>({
        queryKey: [
          ...driveInvalidateKey,
          'entry-activities',
          entryId,
          workspaceId,
        ],
        queryFn: () =>
          get(`drive/file-entries/${entryId}/activities`, {
            workspaceId: workspaceId ?? null,
          }),
      }),
  },
  workspaceStorageInsights: {
    invalidateKey: [...driveInvalidateKey, 'workspace-storage-insights'],
    get: (workspaceId: number) =>
      queryOptions<FetchWorkspaceStorageInsightsResponse>({
        queryKey: [
          ...driveInvalidateKey,
          'workspace-storage-insights',
          workspaceId,
        ],
        queryFn: () => get(`drive/storage-insights`, {workspaceId}),
      }),
  },
  personalDriveInsights: {
    invalidateKey: [...driveInvalidateKey, 'personal-drive-insights'],
    get: (workspaceId?: number | null) =>
      queryOptions<FetchPersonalDriveInsightsResponse>({
        queryKey: [
          ...driveInvalidateKey,
          'personal-drive-insights',
          workspaceId ?? null,
        ],
        queryFn: () =>
          get('drive/personal-insights', {workspaceId: workspaceId ?? null}),
      }),
  },
  folderPath: {
    invalidateKey: [...driveInvalidateKey, 'folders', 'path'],
    get: (hash: string, params: UserFolderPathParams = {}) =>
      queryOptions<{path: DriveFolder[]}>({
        queryKey: ['folder-path', hash, params],
        queryFn: () => get(`folders/${hash}/path`, params),
      }),
  },
  userFolders: {
    invalidateKey: [...driveInvalidateKey, 'folders', 'user'],
    index: (params: UserFoldersApiParams) =>
      queryOptions<UserFoldersResponse>({
        queryKey: ['user-folders', params],
        queryFn: () => get(`users/${params.userId}/folders`, params),
      }),
  },
  shareableLinks: {
    invalidateKey: ['shareable-links'],
    getEntryShareableLink: (entryId: number) =>
      queryOptions<FetchShareableLinkResponse>({
        queryKey: ['shareable-links', 'get-entry', entryId],
        queryFn: () =>
          get(`file-entries/${entryId}/shareable-link`, {
            loader: 'shareableLink',
          }),
      }),
  },
  landingPageData: {
    get: () =>
      queryOptions<Required<Required<BootstrapData>['loaders']>['landingPage']>(
        {
          queryKey: ['landing-page-data'],
          queryFn: () => get('landing-page-data'),
          initialData: () => {
            const data = getBootstrapData().loaders?.landingPage;
            if (data) {
              return data;
            }
          },
        },
      ),
  },
};
